import classNames from "classnames";
import { useState } from "react";
import EmptyState from "@/components/EmptyState";
import Tag from "@/components/Tag";
import { RowHeader, RowLink, TableCard, TableCardRow } from "@/components/Table";
import PersonSideSheet from "@/features/person/components/PersonSideSheet";
import DashboardTableSkeleton from "@/pages/home/_components/DashboardTableSkeleton";
import { useIsDashboardStackedTableRows } from "@/pages/home/_hooks/useIsDashboardSingleColumn";
import {
  DASHBOARD_PANEL_SHELL_CLASS,
  DASHBOARD_TABLE_CONTENT_CELL,
  DASHBOARD_TABLE_META_CELL
} from "@/pages/home/_utils/dashboardPanelStyles";
import {
  DASHBOARD_TABLE_CARD_BODY_CLASS,
  getDashboardPreviewTableClassName
} from "@/pages/home/_utils/dashboardPreviewTable";
import { type Path, useNavigate } from "@/router";
import {
  buildMovementBandPreview,
  formatMovementDateLabel,
  hasAnyMovementRows,
  movementBandLabel,
  type MovementBandGroups,
  type RosterMovementRow
} from "@/features/roster-activity";

const COLUMN_COUNT = 4;
const AVATAR_CELL = "w-14 px-4 py-3 align-middle";

type SideSheetTarget = {
  pgaId: string;
  linkPath?: Path | null;
};

function InitialsAvatar({ initials }: { initials: string }) {
  return (
    <div
      aria-hidden="true"
      className="flex h-10 w-10 min-w-10 items-center justify-center rounded-full bg-sys-brand-secondary-container"
      data-testid="roster-movement-initials-avatar"
    >
      <span className="text-label-large text-sys-brand-on-surface-variant">{initials}</span>
    </div>
  );
}

type MovementRowProps = {
  row: RosterMovementRow;
  onOpen: (row: RosterMovementRow) => void;
};

function MovementStatusTag({ row }: { row: RosterMovementRow }) {
  return (
    <Tag tagType={row.statusTagType} tagStyle="standard" size="small">
      {row.statusLabel}
    </Tag>
  );
}

function MovementRow({ row, onOpen }: MovementRowProps) {
  const isStackedTableRows = useIsDashboardStackedTableRows();
  const isInteractive = Boolean(row.pgaId);

  const openRow = () => {
    if (!row.pgaId) return;
    onOpen(row);
  };

  return (
    <TableCardRow
      columnCount={COLUMN_COUNT}
      rowTestId={`roster-movement-row-${row.id}`}
      onClick={isInteractive ? openRow : undefined}
      ariaLabel={isInteractive ? `View ${row.name}` : undefined}
      stackedLayout={isStackedTableRows}
      mobile={
        <div className="flex min-w-0 gap-3">
          <InitialsAvatar initials={row.initials} />
          <div className="min-w-0 flex-1">
            <div className="text-body-medium text-sys-brand-on-surface">{row.name}</div>
            <div className="text-body-small text-sys-brand-on-surface-variant">{row.subtitle}</div>
            <div className="pt-1">
              <MovementStatusTag row={row} />
            </div>
          </div>
        </div>
      }
    >
      <td className={AVATAR_CELL}>
        <InitialsAvatar initials={row.initials} />
      </td>
      <td className={classNames(DASHBOARD_TABLE_CONTENT_CELL, "w-[28%] max-w-[10rem] truncate")}>{row.name}</td>
      <td className={classNames(DASHBOARD_TABLE_CONTENT_CELL, "text-sys-brand-on-surface-variant")}>{row.subtitle}</td>
      <td className={DASHBOARD_TABLE_META_CELL}>
        <MovementStatusTag row={row} />
      </td>
    </TableCardRow>
  );
}

function InMemoriamRow({ row, onOpen }: MovementRowProps) {
  const isStackedTableRows = useIsDashboardStackedTableRows();
  const dateLabel = formatMovementDateLabel(row.occurredAt);
  const isInteractive = Boolean(row.pgaId);

  const openRow = () => {
    if (!row.pgaId) return;
    onOpen(row);
  };

  return (
    <TableCardRow
      columnCount={COLUMN_COUNT}
      rowTestId={`roster-movement-row-${row.id}`}
      onClick={isInteractive ? openRow : undefined}
      ariaLabel={isInteractive ? `View ${row.name}` : undefined}
      stackedLayout={isStackedTableRows}
      mobile={
        <div className="flex min-w-0 gap-3">
          <InitialsAvatar initials={row.initials} />
          <div className="min-w-0 flex-1">
            <div className="text-body-medium text-sys-brand-on-surface">{row.name}</div>
            <div className="text-body-small text-sys-brand-on-surface-variant">{row.subtitle}</div>
            <div className="text-body-small text-sys-brand-on-surface-variant">{dateLabel}</div>
          </div>
        </div>
      }
    >
      <td className={AVATAR_CELL}>
        <InitialsAvatar initials={row.initials} />
      </td>
      <td className={classNames(DASHBOARD_TABLE_CONTENT_CELL, "w-[28%] max-w-[10rem] truncate")}>{row.name}</td>
      <td className={classNames(DASHBOARD_TABLE_CONTENT_CELL, "text-sys-brand-on-surface-variant")}>{row.subtitle}</td>
      <td className={classNames(DASHBOARD_TABLE_META_CELL, "text-sys-brand-on-surface-variant")}>{dateLabel}</td>
    </TableCardRow>
  );
}

type BandSectionProps = {
  label: string;
  count: number;
  rows: RosterMovementRow[];
  onOpen: (row: RosterMovementRow) => void;
  viewMorePath?: Path;
  viewMoreLabel?: string;
  viewMoreTestId?: string;
  viewMoreRowTestId?: string;
  RowComponent: typeof MovementRow | typeof InMemoriamRow;
};

function BandSection({
  label,
  count,
  rows,
  onOpen,
  viewMorePath,
  viewMoreLabel = "View More",
  viewMoreTestId,
  viewMoreRowTestId,
  RowComponent
}: BandSectionProps) {
  if (rows.length === 0) {
    return null;
  }

  return (
    <>
      <RowHeader label={label} count={count} columnCount={COLUMN_COUNT} rowTestId={`roster-movements-band-${label}`} />
      {rows.map((row) => (
        <RowComponent key={row.id} row={row} onOpen={onOpen} />
      ))}
      {viewMorePath ? (
        <RowLink columnCount={COLUMN_COUNT} to={viewMorePath} testId={viewMoreTestId} rowTestId={viewMoreRowTestId}>
          {viewMoreLabel}
        </RowLink>
      ) : null}
    </>
  );
}

type RosterMovementsPanelProps = {
  sectionId: string;
  bands: MovementBandGroups;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
};

export default function RosterMovementsPanel({
  sectionId,
  bands,
  loading = false,
  error = false,
  onRetry
}: RosterMovementsPanelProps) {
  const navigate = useNavigate();
  const isStackedTableRows = useIsDashboardStackedTableRows();
  const [sideSheet, setSideSheet] = useState<SideSheetTarget | null>(null);

  const joinedPreview = buildMovementBandPreview(bands.joined);
  const leftPreview = buildMovementBandPreview(bands.left);
  const inMemoriamPreview = buildMovementBandPreview(bands.inMemoriam);
  const rosterActivityPath = `/sections/${sectionId}/roster-activity` as Path;
  const isEmpty = !loading && !error && !hasAnyMovementRows(bands);

  const openRow = (row: RosterMovementRow) => {
    if (!row.pgaId) return;
    setSideSheet({
      pgaId: row.pgaId,
      linkPath: row.linkPath
    });
  };

  return (
    <>
      <TableCard
        title="Movements"
        icon="swap_horiz"
        testId="dashboard-roster-panel"
        shellClassName={DASHBOARD_PANEL_SHELL_CLASS}
        bodyClassName={DASHBOARD_TABLE_CARD_BODY_CLASS}
      >
        <table
          className={getDashboardPreviewTableClassName(isStackedTableRows)}
          data-testid="dashboard-roster-movements-table"
          aria-label="Section roster movements"
        >
          {isStackedTableRows ? null : (
            <colgroup>
              <col className="w-14" />
              <col className="w-[28%]" />
              <col />
              <col className="w-28" />
            </colgroup>
          )}
          <tbody>
            {loading ? (
              <DashboardTableSkeleton columnCount={COLUMN_COUNT} />
            ) : error ? (
              <tr>
                <td colSpan={COLUMN_COUNT}>
                  <EmptyState
                    iconName="error"
                    title="Unable to Load Roster Movements"
                    caption="Try refreshing the page. If the problem continues, contact support."
                    action={onRetry ? { children: "Try Again", onClick: onRetry } : undefined}
                  />
                </td>
              </tr>
            ) : isEmpty ? (
              <tr>
                <td colSpan={COLUMN_COUNT}>
                  <EmptyState
                    iconName="group_off"
                    title="No Roster Movements"
                    caption="Members who joined, left, or were memorialized in the last 30 days will appear here."
                    action={{
                      children: "View Roster Activity",
                      to: "/sections/:sectionId/roster-activity",
                      params: { sectionId }
                    }}
                  />
                </td>
              </tr>
            ) : (
              <>
                <BandSection
                  label={movementBandLabel("joined")}
                  count={joinedPreview.total}
                  rows={joinedPreview.preview}
                  onOpen={openRow}
                  viewMorePath={rosterActivityPath}
                  viewMoreLabel="View more joined section movements"
                  viewMoreTestId="roster-joined-section-view-more"
                  viewMoreRowTestId="roster-joined-section-view-more-row"
                  RowComponent={MovementRow}
                />
                <BandSection
                  label={movementBandLabel("left")}
                  count={leftPreview.total}
                  rows={leftPreview.preview}
                  onOpen={openRow}
                  viewMorePath={rosterActivityPath}
                  viewMoreLabel="View more left section movements"
                  viewMoreTestId="roster-left-section-view-more"
                  viewMoreRowTestId="roster-left-section-view-more-row"
                  RowComponent={MovementRow}
                />
                <BandSection
                  label={movementBandLabel("inMemoriam")}
                  count={inMemoriamPreview.total}
                  rows={inMemoriamPreview.preview}
                  onOpen={openRow}
                  viewMorePath={rosterActivityPath}
                  viewMoreLabel="View more in memoriam movements"
                  viewMoreTestId="roster-in-memoriam-view-more"
                  viewMoreRowTestId="roster-in-memoriam-view-more-row"
                  RowComponent={InMemoriamRow}
                />
              </>
            )}
          </tbody>
        </table>
      </TableCard>
      <PersonSideSheet
        open={!!sideSheet}
        onClose={() => setSideSheet(null)}
        pgaId={sideSheet?.pgaId ?? null}
        showPersonalDetails
        showEmployment
        customActionButtons={
          sideSheet?.linkPath
            ? [
                {
                  text: "View Person",
                  variant: "outlined",
                  testId: "roster-movements-view-person",
                  onClick: () => {
                    const linkPath = sideSheet.linkPath;
                    if (!linkPath) return;
                    navigate(linkPath);
                    setSideSheet(null);
                  }
                }
              ]
            : undefined
        }
      />
    </>
  );
}
